import { FC, Fragment, useCallback, useMemo, useRef, useState } from "react"
import { useNavigate } from "react-router-dom"
import Table from "../../table"
import BrandCell from "./cells/brand"
import LeadsCell from "./cells/leads"
import OwnerCell from "./cells/owner"
import MenuCell from "./cells/menu"
import PushToEngagements from "../push-to-engagements"
import LeadStats, { ChartClickData } from "../lead-stats"
import FilterChips from "../../charts/filter-chips"
import {
  useFetchLeadLogsQuery,
  useUpdateSubscriptionMutation
} from "../../../../redux/queries"
import { useAppDispatch, useAppSelector } from "../../../hooks"
import {
  selectTabRange,
  updateRange
} from "../../../../redux/slices/engagements"
import {
  convertSubscriptionDetailsToPayload,
  dateFilterToTimestamps
} from "../../../utils"
import {
  BBColumnDef,
  SubscriptionDetails,
  LeadLog,
  EngagementsTab
} from "../../../../types"
import DateFilter, {
  DateFilterHandle,
  DateRangePreset
} from "../../activity/date-filter"
import NoResultsInRangeMessage from "../../activity/no-results-in-range-message"

interface Props {
  loid: string | undefined
  orgId?: string
  isSuperAdmin?: boolean
}

const LeadsTable: FC<Props> = ({ loid, orgId, isSuperAdmin }) => {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const [updateSubscription] = useUpdateSubscriptionMutation()

  const [selectedLeadLog, setSelectedLeadLog] = useState<LeadLog>()
  const dateFilterRef = useRef<DateFilterHandle>(null)
  const [chartFilter, setChartFilter] = useState<ChartClickData | null>(null)

  const handleClick = useCallback(
    (id: string) => navigate(`/leads/${id}`),
    [navigate]
  )

  const handleEnrollToRateAlerts = useCallback(
    (subscription?: SubscriptionDetails) => {
      if (!subscription) return

      updateSubscription({
        ...convertSubscriptionDetailsToPayload(subscription),
        id: subscription.id,
        active: subscription.active === "1" ? "0" : "1"
      })
    },
    [updateSubscription]
  )

  const handleCreateEngagement = (leadLog: LeadLog) => {
    setSelectedLeadLog(leadLog)
  }

  const cancelCreateEngagement = () => setSelectedLeadLog(undefined)

  const columns: BBColumnDef<LeadLog>[] = useMemo(
    () => [
      {
        id: "leads",
        header: "Leads",
        accessorFn: row => `${row.name} ${row.email}`,
        footer: props => props.column.id,
        cell: props => (
          <LeadsCell
            info={props.row.original}
            onClick={() => handleClick(props.row.original.id)}
          />
        )
      },
      {
        id: "brand",
        header: "Brand",
        accessorFn: row => row.brand_name,
        footer: props => props.column.id,
        cell: props => (
          <BrandCell
            info={props.row.original}
            onClick={() => handleClick(props.row.original.id)}
          />
        ),
        meta: { headerClassName: "hidden md:table-cell" }
      },
      {
        id: "owner",
        header: "Owner",
        accessorFn: row => row.owner_email,
        footer: props => props.column.id,
        cell: props => (
          <OwnerCell
            info={props.row.original}
            onClick={() => handleClick(props.row.original.id)}
          />
        ),
        meta: { headerClassName: "hidden md:table-cell" }
      },
      {
        id: "timestamp",
        header: "",
        accessorFn: row => row.timestamp,
        footer: props => props.column.id,
        cell: props => null,
        meta: { headerClassName: "hidden" }
      },
      {
        id: "edit",
        header: "",
        footer: props => props.column.id,
        enableSorting: false,
        cell: props => (
          <MenuCell
            info={props.row.original}
            onPushToEngagement={handleCreateEngagement}
            onEnrollToRateAlerts={handleEnrollToRateAlerts}
          />
        )
      }
    ],
    [handleClick, handleEnrollToRateAlerts]
  )

  const tabKey = EngagementsTab.leads
  const dateFilter = useAppSelector(state => selectTabRange(state, tabKey))

  const handleDateRangeChange = useCallback(
    (range: DateRangePreset, customStartDate?: Date, customEndDate?: Date) => {
      dispatch(
        updateRange({
          tabKey,
          range,
          customStartDate: customStartDate || null,
          customEndDate: customEndDate || null
        })
      )
      // Clear chart filter when manually changing date range
      setChartFilter(null)
    },
    [dispatch, tabKey]
  )

  const handleClearDateFilter = useCallback(() => {
    dispatch(
      updateRange({
        tabKey,
        range: "all",
        customStartDate: null,
        customEndDate: null
      })
    )
  }, [dispatch, tabKey])

  const handleViewLast12Months = useCallback(() => {
    const now = new Date()
    const lastYear = new Date(now)
    lastYear.setDate(now.getDate() - 365)
    lastYear.setHours(0, 0, 0, 0)
    dispatch(
      updateRange({
        tabKey,
        range: "custom",
        customStartDate: lastYear,
        customEndDate: now
      })
    )
  }, [dispatch, tabKey])

  // Handle chart click to update date filter
  const handleChartClick = useCallback(
    (data: ChartClickData) => {
      setChartFilter(data)

      if (data.viewType === "daily") {
        const date = new Date(data.period)
        dispatch(
          updateRange({
            tabKey,
            range: "custom",
            customStartDate: date,
            customEndDate: date
          })
        )
      } else {
        // Monthly view: "2025-January"
        const [year, monthName] = data.period.split("-")
        const monthMap: Record<string, number> = {
          January: 0,
          February: 1,
          March: 2,
          April: 3,
          May: 4,
          June: 5,
          July: 6,
          August: 7,
          September: 8,
          October: 9,
          November: 10,
          December: 11
        }
        const month = monthMap[monthName]

        const startOfMonth = new Date(parseInt(year), month, 1)
        const endOfMonth = new Date(parseInt(year), month + 1, 0)

        dispatch(
          updateRange({
            tabKey,
            range: "custom",
            customStartDate: startOfMonth,
            customEndDate: endOfMonth
          })
        )
      }
    },
    [dispatch, tabKey]
  )

  const handleClearChartFilter = useCallback(() => {
    setChartFilter(null)
  }, [])

  // Calculate timestamps from date range
  const { min: timestamp_min, max: timestamp_max } = useMemo(
    () => dateFilterToTimestamps(dateFilter),
    [dateFilter]
  )

  // lead logs
  const { data: leadLogs, isFetching } = useFetchLeadLogsQuery({
    limit: 99999,
    offset: 0,
    ...(timestamp_min !== undefined && timestamp_max !== undefined
      ? { min_created: timestamp_min, max_created: timestamp_max }
      : {}),
    owner: loid,
    org_id: orgId,
    isSuperAdmin
  })

  // All-time existence check (limit 1) — only when current range is empty and not "all"
  const isRangeEmpty = !isFetching && (leadLogs?.length ?? 0) === 0
  const isNotAllTimeRange = dateFilter.range !== "all"
  const { data: anyHistoricalLeads = [], isFetching: isFetchingAllTime } =
    useFetchLeadLogsQuery(
      {
        limit: 1,
        offset: 0,
        owner: loid,
        org_id: orgId,
        isSuperAdmin
      },
      { skip: !isRangeEmpty || !isNotAllTimeRange }
    )
  const hasAnyHistorical = (anyHistoricalLeads?.length ?? 0) > 0
  const isCheckingHistorical =
    isRangeEmpty && isNotAllTimeRange && isFetchingAllTime
  const shouldHideHeader = !isFetching && (leadLogs?.length ?? 0) === 0

  return (
    <>
      <Table
        isLoading={isFetching}
        data={leadLogs ?? []}
        columns={columns}
        viewType="table"
        dropdownFilterColumnId="leads"
        autoResetPageIndex={false}
        searchPlaceholder="Search website leads…"
        extraHeaderElement={
          <DateFilter
            ref={dateFilterRef}
            selectedRange={dateFilter.range}
            customStartDate={dateFilter.customStartDate}
            customEndDate={dateFilter.customEndDate}
            onRangeChange={handleDateRangeChange}
            onClearFilter={handleClearDateFilter}
          />
        }
        extraHeaderElementClassName="ml-auto"
        shrinkFilterSort
        defaultSortBy="timestamp"
        defaultSortDir="desc"
        extraElementBeforeTable={
          <Fragment>
            <LeadStats
              orgId={isSuperAdmin ? undefined : orgId}
              loid={isSuperAdmin ? undefined : loid}
              onChartClick={handleChartClick}
              selectedBarIndex={chartFilter?.index ?? null}
            />
            {chartFilter && (
              <FilterChips
                filter={chartFilter}
                onClear={handleClearChartFilter}
                currentCount={leadLogs?.length ?? 0}
                totalCount={leadLogs?.length ?? 0}
                itemLabel="leads"
              />
            )}
          </Fragment>
        }
        hasColumnFilter={false}
        noHeader={shouldHideHeader}
        noDataElement={
          isCheckingHistorical ? (
            <td colSpan={columns.length} className="p-0">
              <div className="min-h-[400px]" />
            </td>
          ) : isRangeEmpty && hasAnyHistorical && !isFetchingAllTime ? (
            <td colSpan={columns.length} className="p-0">
              <NoResultsInRangeMessage
                type="leads"
                onViewLast12Months={handleViewLast12Months}
                onSelectDateRange={() => dateFilterRef.current?.openAndFocus()}
              />
            </td>
          ) : undefined
        }
      />
      <PushToEngagements
        brandId={selectedLeadLog?.group_id}
        leadId={selectedLeadLog?.id}
        onCancel={cancelCreateEngagement}
      />
    </>
  )
}

export default LeadsTable
